import { useEffect, useState } from 'react';
import { FileText, Link2, Loader2, Play, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import {
  adminHideSharedResource,
  adminShowSharedResource,
  listSharedResources,
} from '@/features/webinar/webinarThunks';
import { fetchAdminResourceState, triggerAdminEvent } from '@/features/live/liveThunks';
import { INTERACTION_RESOURCE_TYPES } from '@/lib/liveResourceExclusivity';
import { Pagination } from '@/components/ui/pagination';

export default function SharedResourceLiveList({
  workspaceId,
  webinarId,
  sessionId,
  sessionIdentifier,
  visualIdentity,
}: {
  workspaceId: number;
  webinarId: number;
  sessionId?: number;
  sessionIdentifier: string;
  visualIdentity: WorkspaceVisualIdentity;
}) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();

  const sharedResources = useAppSelector((s: RootState) => s.webinar.sharedResources);
  const sharedResourcesTotalPages = useAppSelector(
    (s: RootState) => s.webinar.sharedResourcesTotalPages,
  );
  const adminResourceStates = useAppSelector((s: RootState) => s.live.adminResourceStates);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    void dispatch(listSharedResources({ workspaceId, webinarId, page }));
  }, [dispatch, workspaceId, webinarId, page]);

  useEffect(() => {
    if (!sessionId) return;
    void dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));
  }, [dispatch, workspaceId, webinarId, sessionId]);

  const liveSharedResourceStates = adminResourceStates.filter(
    (s) => s.resourceType === 'SHARED_RESOURCE' && s.live,
  );
  const liveIds = new Set(liveSharedResourceStates.map((s) => s.resourceId));

  const handleToggle = async (sharedResourceId: number, isLive: boolean) => {
    if (!sessionIdentifier) return;
    setLoadingId(sharedResourceId);
    try {
      if (isLive) {
        await dispatch(
          adminHideSharedResource({ workspaceId, webinarId, sessionIdentifier, sharedResourceId }),
        ).unwrap();
        if (sessionId) {
          void dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));
        }
      } else {
        const interactionsToHide = adminResourceStates.filter(
          (s) => s.live && INTERACTION_RESOURCE_TYPES.includes(s.resourceType),
        );
        for (const state of interactionsToHide) {
          await dispatch(
            triggerAdminEvent({
              workspaceId,
              webinarId,
              sessionIdentifier,
              input: {
                eventType: 'RESOURCE_HIDE',
                resourceType: state.resourceType,
                resourceId: state.resourceId,
                payloadJson: JSON.stringify({ timeNow: new Date().getTime() }),
              },
            }),
          );
        }
        if (interactionsToHide.length > 0 && sessionId) {
          void dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));
        }

        const otherSharedToHide = liveSharedResourceStates.filter(
          (s) => s.resourceId !== sharedResourceId,
        );
        for (const state of otherSharedToHide) {
          await dispatch(
            adminHideSharedResource({
              workspaceId,
              webinarId,
              sessionIdentifier,
              sharedResourceId: state.resourceId,
            }),
          );
        }

        await dispatch(
          adminShowSharedResource({ workspaceId, webinarId, sessionIdentifier, sharedResourceId }),
        ).unwrap();
        if (sessionId) {
          void dispatch(fetchAdminResourceState({ workspaceId, webinarId, sessionId }));
        }
      }
    } catch (error) {
      console.error('Error toggling shared resource live:', error);
      toast.error(t('sharedResourcesForm.errors.generic'));
    } finally {
      setLoadingId(null);
    }
  };

  const isDark = visualIdentity?.theme === 'DARK';

  return (
    <div className="flex flex-col gap-3" style={{ color: isDark ? '#FFFFFF' : '#000000' }}>
      {sharedResources.length === 0 ? (
        <p className="text-sm opacity-80">{t('sharedResourcesForm.empty')}</p>
      ) : (
        sharedResources.map((resource) => {
          const isLive = liveIds.has(resource.id);
          const isFile = resource.type === 'FILE';
          const isReady = resource.status === 'READY';
          const buttonBg = visualIdentity?.contrastColor || '#2356CC';
          return (
            <div
              key={resource.id}
              className="flex items-start gap-3 rounded-none border-0 lg:rounded-2xl lg:border p-4"
              style={{
                backgroundColor: isDark ? '#1B1D2A' : '#FFFFFF',
                borderColor: isDark ? '#2E3142' : '#EAEAEC',
              }}
            >
              <div className="flex-1 min-w-0 flex items-start gap-2">
                <div className="size-9 shrink-0 bg-medium rounded-lg flex items-center justify-center">
                  {isFile ? (
                    <FileText className="size-4 text-black" />
                  ) : (
                    <Link2 className="size-4 text-black" />
                  )}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-medium line-clamp-2 wrap-break-word">
                    {resource.title}
                  </span>
                  {resource.description && (
                    <span className="text-xs opacity-70 line-clamp-2 wrap-break-word">
                      {resource.description}
                    </span>
                  )}
                </div>
              </div>
              {/* Diffuser / masquer */}
              <button
                type="button"
                aria-label={isLive ? t('sharedResourcesLive.hide') : t('sharedResourcesLive.show')}
                className="shrink-0 rounded-lg p-2.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => handleToggle(resource.id, isLive)}
                disabled={!isReady || loadingId === resource.id}
                title={!isReady ? t('sharedResourcesLive.notReady') : undefined}
                style={{ backgroundColor: isLive ? '#FFD753' : buttonBg }}
              >
                {loadingId === resource.id ? (
                  <Loader2
                    size={18}
                    color={isLive ? '#000000' : '#FFFFFF'}
                    className="animate-spin"
                  />
                ) : isLive ? (
                  <X size={18} color="#000000" />
                ) : (
                  <Play size={18} color="#FFFFFF" />
                )}
              </button>
            </div>
          );
        })
      )}
      <Pagination
        currentPage={page}
        totalPages={sharedResourcesTotalPages}
        onPageChange={setPage}
      />
    </div>
  );
}
